import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { checklistCategoryOptions } from "@/constants/checklist-categories";
import { useSiteDocumentationCreateMutation, useSiteDocumentationUpdateMutation } from "@/features/dashboard/api/queries/site-documentation-query";
import type { SiteDocumentation } from "@/features/dashboard/types/site-documentation";
import { siteDocumentationSchema } from "@/features/dashboard/types/site-documentation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useModalControl } from "../core/modals/use-modal-controller";

interface SiteDocumentationModalProps {
	data?: SiteDocumentation;
	siteId: string;
	onSuccess?: () => void;
}

export default function SiteDocumentationModal({ data, siteId, onSuccess }: SiteDocumentationModalProps) {
	const isEditing = Boolean(data?.id);
	const { closeModal } = useModalControl();
	const createMutation = useSiteDocumentationCreateMutation();
	const updateMutation = useSiteDocumentationUpdateMutation(String(data?.id));

	const form = useForm<SiteDocumentation>({
		resolver: zodResolver(siteDocumentationSchema),
		defaultValues: data
			? {
					...data,
					site_id: data.site_id || siteId,
				}
			: {
					id: undefined,
					site_id: siteId,
					category: "",
					documentation_date: new Date().toISOString().split("T")[0],
					created_by: null,
					mir_count: 0,
					created_at: null,
					updated_at: null,
				},
	});

	const handleSubmit = async (values: SiteDocumentation) => {
		try {
			const submitData = {
				...values,
				site_id: siteId,
			};

			if (isEditing) {
				await updateMutation.mutateAsync(submitData);
				toast.success("Site documentation updated successfully");
			} else {
				await createMutation.mutateAsync(submitData);
				toast.success("Site documentation created successfully");
			}

			onSuccess?.();
			closeModal();
		} catch (error) {
			console.error(error);
			toast.error(`Failed to ${isEditing ? "update" : "create"} site documentation`);
		}
	};

	const isSubmitting = createMutation.isPending || updateMutation.isPending;

	return (
		<Form {...form}>
			<form id="site-documentation-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="category"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Category</FormLabel>
										<FormGroup>
											<Select placeholder="Select category" options={checklistCategoryOptions} defaultValue={field.value} onValueChange={field.onChange} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="documentation_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Documentation Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="mir_count"
								render={({ field }) => (
									<FormItem>
										<FormLabel>MIR Count</FormLabel>
										<FormGroup>
											<Input className="input" type="number" min={0} {...field} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-end gap-2 pt-4 border-t">
					<Button type="submit" disabled={isSubmitting}>
						{isSubmitting ? "Saving..." : isEditing ? "Update Documentation" : "Create Documentation"}
					</Button>
				</div>
			</form>
		</Form>
	);
}
